import { useRef, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { useUploadSparkActivityCsv } from '../../hooks/useSparkActivities';
import type { BatchUploadResult } from '../../types/spark-activity';

interface BatchUploadDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function BatchUploadDialog({ open, onClose }: BatchUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<BatchUploadResult | null>(null);

  const uploadMutation = useUploadSparkActivityCsv();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    try {
      const data = await uploadMutation.mutateAsync(selectedFile);
      setResult(data);
    } catch {
      // error shown via toast in mutation hook
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div role="dialog" aria-label="Upload CSV Batch" className="bg-pine-card border border-pine-line rounded-pine-card w-full max-w-md p-6 shadow-pine-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-pine-ink font-display">Upload CSV Batch</h2>
          <button
            onClick={handleClose}
            className="text-pine-ink-soft hover:text-pine-ink transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!result ? (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-pine-line rounded-pine-card p-6 text-center cursor-pointer hover:border-pine-pine transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 mx-auto mb-2 text-pine-ink-soft" />
              <p className="text-sm text-pine-ink">
                {selectedFile ? selectedFile.name : 'Click to select a CSV file'}
              </p>
              {selectedFile && (
                <p className="text-xs text-pine-ink-soft mt-1">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm text-pine-ink-soft hover:text-pine-ink transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || uploadMutation.isPending}
                className="px-4 py-2 text-sm bg-pine-gold hover:bg-pine-gold-deep text-[#2A1F08] font-semibold disabled:opacity-50 disabled:cursor-not-allowed rounded-pine-input shadow-[0_2px_8px_rgba(232,161,60,.4)] transition-colors"
              >
                {uploadMutation.isPending ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-pine-l1 border border-pine-approved rounded-pine-card p-4">
              <p className="text-pine-approved font-medium">Import complete</p>
              <p className="text-sm text-pine-ink mt-1">
                Batch: <span className="font-semibold">{result.batchName}</span>
              </p>
              <p className="text-sm text-pine-ink">
                Imported: <span className="font-semibold">{result.imported}</span>
                {result.skipped > 0 && (
                  <span className="ml-2 text-pine-ink-soft">({result.skipped} skipped)</span>
                )}
              </p>
            </div>

            {Object.keys(result.notMatched).length > 0 && (
              <div className="bg-white border border-pine-review rounded-pine-card p-4">
                <p className="text-pine-review font-medium text-sm mb-1">Unmatched fields</p>
                <p className="text-xs text-pine-ink-soft">
                  {Object.keys(result.notMatched).length} activities had unmatched lookup values.
                  Check the processed JSON in S3 for details.
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm bg-white border-[1.5px] border-pine-pine text-pine-pine font-semibold hover:bg-pine-hover-button rounded-pine-input transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
